/**
 * 国家（地区）管理前端接口
 * @author ChaseZhang
 * @since 2025-06-23
 */

import { httpClient as request } from '@/utils/http'

// ==================== 类型定义 ====================

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 禁用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

/**
 * 国家（地区）VO模型
 */
export interface CountryVO {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 英文名 */
  countryNameEn: string;
  /** 中文名 */
  countryNameCn: string;
  /** 国家或地区的ISO代码 */
  countryCode: string;
  /** 大洲ID */
  continentId?: string;
  /** 区号 */
  areaCode?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled: EnabledStatus;
  /** 创建时间 */
  dateCreated?: string;
  /** 修改时间 */
  dateModified?: string;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** EDI代码列表 */
  ediCodes?: CommonEdiCodeVO[];
}

/**
 * 通用EDI代码VO模型
 */
export interface CommonEdiCodeVO {
  /** EDI代码ID */
  id: string;
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
  /** EDI类型 */
  ediType: number;
  /** 业务实体代码 */
  businessEntityCode: string;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 国家（地区）查询请求参数
 */
export interface CountryQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 租户ID */
  tenantId?: string;
  /** 关键词搜索 */
  keyword?: string;
  /** 英文名（模糊查询） */
  countryNameEn?: string;
  /** 中文名（模糊查询） */
  countryNameCn?: string;
  /** 国家代码（精确查询） */
  countryCode?: string;
  /** 大洲ID */
  continentId?: string;
  /** 区号 */
  areaCode?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled?: EnabledStatus;
  /** 区域 */
  region?: string;
}

/**
 * 国家（地区）保存请求参数
 */
export interface CountrySaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 租户ID */
  tenantId?: string;
  /** 英文名 */
  countryNameEn: string;
  /** 中文名 */
  countryNameCn: string;
  /** 国家或地区的ISO代码 */
  countryCode: string;
  /** 大洲ID */
  continentId?: string;
  /** 区号 */
  areaCode?: string;
  /** 国家启用状态: 1-启用, 0-禁用 */
  enabled?: EnabledStatus;
}

/**
 * 模糊搜索请求参数
 */
export interface FuzzySearchRequest {
  /** 搜索关键字 */
  keyword: string;
  /** 租户ID */
  tenantId?: string;
  /** 启用状态 */
  enabled?: EnabledStatus;
  /** 国家类型 */
  type?: string;
  /** 大洲ID */
  continentId?: string;
}

/**
 * 分页响应结果
 */
export interface PageResultVO<T> {
  /** 当前页码 */
  pageNum: number;
  /** 每页大小 */
  pageSize: number;
  /** 总记录数 */
  total: number;
  /** 数据列表 */
  records: T[];
}

// ==================== API 类 ====================

/**
 * 国家（地区）管理API
 */
export class CountryApi {
  private static readonly BASE_URL = '/mdm/country'

  // ==================== 核心CRUD接口 ====================

  /**
   * 分页查询国家列表
   */
  static queryPage = async(params: CountryQueryRequest): Promise<PageResultVO<CountryVO>> => {
    return request.post(`${CountryApi.BASE_URL}/queryPage`, params)
  }

  /**
   * 查询国家列表（不分页）
   */
  static queryList = async(params: CountryQueryRequest): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryList`, params)
  }

  /**
   * 模糊搜索国家
   */
  static search = async(params: FuzzySearchRequest): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/search`, params)
  }

  /**
   * 根据ID查询国家详情
   */
  static getById = async(id: string): Promise<CountryVO> => {
    return request.post(`${CountryApi.BASE_URL}/getById`, { id: String(id) })
  }

  /**
   * 保存国家（新增或更新）
   */
  static save = async(params: CountrySaveRequest): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/save`, params)
  }

  /**
   * 删除国家
   */
  static delete = async(id: string): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/delete`, { id: String(id) })
  }

  // ==================== 批量操作接口 ====================

  /**
   * 批量保存国家
   */
  static batchSave = async(requests: CountrySaveRequest[]): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/batchSave`, requests)
  }

  /**
   * 批量删除国家
   */
  static batchDelete = async(ids: string[]): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/batchDelete`, { ids: ids.map(String) })
  }

  /**
   * 更新国家状态
   */
  static updateStatus = async(ids: string[], enabled: EnabledStatus): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/updateStatus`, { ids: ids.map(String), enabled })
  }

  /**
   * 批量更新国家状态
   */
  static batchUpdateStatus = async(ids: string[], enabled: EnabledStatus): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/batchUpdateStatus`, { ids: ids.map(String), enabled })
  }

  // ==================== 专项查询接口 ====================

  /**
   * 根据国家代码查询国家
   */
  static queryByCode = async(countryCode: string): Promise<CountryVO> => {
    return request.post(`${CountryApi.BASE_URL}/queryByCode`, { countryCode })
  }

  /**
   * 根据大洲ID查询国家列表
   */
  static queryByContinent = async(continentId: string): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryByContinent`, { continentId: String(continentId) })
  }

  /**
   * 查询启用状态的国家列表
   */
  static queryEnabled = async(): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryEnabled`, {})
  }

  /**
   * 根据国家名称查询
   */
  static queryByName = async(nameCn?: string, nameEn?: string, enabled?: EnabledStatus): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryByName`, { nameCn, nameEn, enabled })
  }

  /**
   * 根据区域查询国家
   */
  static queryByRegion = async(region: string, enabled?: EnabledStatus): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryByRegion`, { region, enabled })
  }

  // ==================== 平台关联接口 ====================

  /**
   * 查询可关联的平台国家（分页）
   */
  static queryPlatformPage = async(params: CountryQueryRequest): Promise<PageResultVO<CountryVO>> => {
    return request.post(`${CountryApi.BASE_URL}/queryPlatformPage`, params)
  }

  /**
   * 查询可关联的平台国家（列表）
   */
  static queryPlatformList = async(params: CountryQueryRequest): Promise<CountryVO[]> => {
    return request.post(`${CountryApi.BASE_URL}/queryPlatformList`, params)
  }

  /**
   * 批量关联平台国家
   */
  static batchAssociate = async(platformIds: string[]): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/batchAssociate`, { platformIds: platformIds.map(String) })
  }

  /**
   * 批量解除关联平台国家
   */
  static batchDisassociate = async(platformIds: string[]): Promise<boolean> => {
    return request.post(`${CountryApi.BASE_URL}/batchDisassociate`, { platformIds: platformIds.map(String) })
  }
}

// ==================== 便捷服务 ====================

/**
 * 国家（地区）管理服务
 */
export const CountryService = {
  // 核心CRUD
  queryPage: CountryApi.queryPage,
  queryList: CountryApi.queryList,
  search: CountryApi.search,
  getById: CountryApi.getById,
  save: CountryApi.save,
  delete: CountryApi.delete,

  // 批量操作
  batchSave: CountryApi.batchSave,
  batchDelete: CountryApi.batchDelete,
  updateStatus: CountryApi.updateStatus,
  batchUpdateStatus: CountryApi.batchUpdateStatus,

  // 专项查询
  queryByCode: CountryApi.queryByCode,
  queryByContinent: CountryApi.queryByContinent,
  queryEnabled: CountryApi.queryEnabled,
  queryByName: CountryApi.queryByName,
  queryByRegion: CountryApi.queryByRegion,

  // 平台关联
  queryPlatformPage: CountryApi.queryPlatformPage,
  queryPlatformList: CountryApi.queryPlatformList,
  batchAssociate: CountryApi.batchAssociate,
  batchDisassociate: CountryApi.batchDisassociate
}

export default CountryApi

// 兼容性导出
export type CountryModel = CountryVO